/**
 * AIInterviewConversation - Chat UI + Entity Sidebar (split panel)
 * Real-time conversation with AI interviewer and live entity extraction
 */

import { useState, useRef, useEffect } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Send,
  Loader2,
  Bot,
  User,
  Sparkles,
  CheckCircle2,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { DOMAIN_COLORS } from '@/lib/constants/domainColors'
import type { AIInterviewMessage, LiveExtractedEntity, AIInterviewPlan } from '@ispot/shared'

// ============================================================================
// COMPONENT
// ============================================================================

interface AIInterviewConversationProps {
  messages: AIInterviewMessage[]
  entities: LiveExtractedEntity[]
  mcqOptions: string[]
  plan: AIInterviewPlan | null
  isStreaming: boolean
  personName: string | null
  onSendMessage: (content: string, inputMethod?: 'text' | 'mcq', selectedMcqOption?: string) => void
  onComplete: () => void
}

export function AIInterviewConversation({
  messages,
  entities,
  mcqOptions,
  plan,
  isStreaming,
  personName,
  onSendMessage,
  onComplete,
}: AIInterviewConversationProps) {
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (!isStreaming) inputRef.current?.focus()
  }, [isStreaming])

  const handleSend = () => {
    if (!input.trim() || isStreaming) return
    onSendMessage(input.trim())
    setInput('')
  }

  const handleMcqClick = (option: string) => {
    if (isStreaming) return
    onSendMessage(option, 'mcq', option)
  }

  return (
    <div className="flex h-full gap-4 bg-bg p-4 max-lg:flex-col">
      {/* Chat Panel */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-border bg-bg-card">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="text-body font-semibold text-fg">
              {personName ? `Interview with ${personName}` : 'Quick interview'}
            </h2>
            <p className="text-caption text-fg-muted">
              Skerpa asks the questions. Extracted evidence appears on the right.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={onComplete} disabled={isStreaming} className="border-border">
            <CheckCircle2 className="w-4 h-4 mr-1" /> End
          </Button>
        </div>
        {/* Messages */}
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                'flex max-w-[85%] gap-2.5',
                msg.role === 'user' ? 'ml-auto flex-row-reverse' : '',
              )}
            >
              <div className={cn(
                'mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full',
                msg.role === 'ai' ? 'bg-accent-50 text-accent-500' : 'bg-primary-500 text-bg-card',
              )}>
                {msg.role === 'ai' ? <Bot className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
              </div>
              <div className={cn(
                'rounded-md px-3.5 py-2.5 text-body leading-relaxed',
                msg.role === 'ai'
                  ? 'border border-border bg-bg-card text-fg'
                  : 'bg-primary-500 text-bg-card',
              )}>
                {msg.content || (
                  <span className="flex items-center gap-1.5 text-fg-muted">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking...
                  </span>
                )}
                {msg.role === 'user' && msg.inputMethod === 'mcq' && (
                  <Badge variant="outline" className="mt-1.5 text-[10px] opacity-60">MCQ</Badge>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* MCQ Options */}
        {mcqOptions.length > 0 && !isStreaming && (
          <div className="flex flex-wrap gap-2 border-t border-border px-4 py-3">
            {mcqOptions.map((option) => (
              <button
                key={option}
                onClick={() => handleMcqClick(option)}
                className="rounded-full border border-border bg-bg-card px-3 py-1.5 text-body text-fg-secondary transition-colors hover:border-accent-500/50 hover:bg-accent-50/50 hover:text-fg"
              >
                {option}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="flex gap-2 border-t border-border bg-bg-card p-3">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder={isStreaming ? 'AI is responding...' : 'Type your response...'}
            disabled={isStreaming}
            className="flex-1 rounded-md border border-border bg-bg-card px-3 py-2 text-body text-fg outline-none placeholder:text-fg-muted focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15 disabled:opacity-50"
          />
          <Button size="sm" onClick={handleSend} disabled={!input.trim() || isStreaming} className="bg-accent-500 text-bg-card hover:bg-accent-600">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Entity Sidebar */}
      <div className="flex w-72 flex-shrink-0 flex-col gap-3 max-lg:w-full">
        {/* Entities */}
        <Card className="flex-1 overflow-y-auto rounded-md border border-border bg-bg-card p-3 shadow-none">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-accent-500" />
            <h3 className="text-body font-medium text-fg">
              Extracted Entities ({entities.length})
            </h3>
          </div>
          {entities.length === 0 ? (
            <p className="py-4 text-center text-caption text-fg-muted">
              Entities will appear here as the conversation progresses
            </p>
          ) : (
            <div className="space-y-1.5">
              {entities.map((entity, i) => {
                const colors = DOMAIN_COLORS[entity.domain || 'market']
                return (
                  <div
                    key={`${entity.type}-${entity.name}-${i}`}
                    className={cn(
                      'flex items-start gap-2 rounded-md border p-2 text-caption',
                      colors?.bg || 'bg-bg-secondary',
                      colors?.border || 'border-border',
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium text-fg">{entity.name}</div>
                      <div className={cn('text-[10px]', colors?.text || 'text-fg-muted')}>
                        {entity.type}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[9px] flex-shrink-0">
                      {Math.round((entity.confidence || 0) * 100)}%
                    </Badge>
                  </div>
                )
              })}
            </div>
          )}
        </Card>

        {/* Plan Progress */}
        {plan && (
          <Card className="rounded-md border border-border bg-bg-card p-3 shadow-none">
            <h3 className="mb-2 text-caption font-medium text-fg">Interview Plan</h3>
            <div className="space-y-1">
              {plan.questions.slice(0, 6).map((q, i) => (
                <div key={q.id} className="flex items-center gap-1.5 text-[11px] text-fg-muted">
                  <ChevronRight className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{q.text}</span>
                  <Badge variant="outline" className="text-[9px] ml-auto flex-shrink-0">
                    {q.type}
                  </Badge>
                </div>
              ))}
              {plan.questions.length > 6 && (
                <div className="pl-4 text-[10px] text-fg-muted">
                  +{plan.questions.length - 6} more questions
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}
